import { Injectable, signal } from '@angular/core';
import { getCookie, removeCookie, setCookie } from 'typescript-cookie';
import { decodeJwt, isTokenExpired } from '@shared/utils/jwt.utils';

const TOKEN_KEY = 'ccs-payments-token';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private readonly _hasSession = signal(!!getCookie(TOKEN_KEY));
  private readonly _sessionEndCallbacks = new Set<() => void>();

  /** Whether an authenticated session is available to session-scoped resources. */
  readonly hasSession = this._hasSession.asReadonly();

  saveToken(token: string) {
    setCookie(TOKEN_KEY, token, { expires: 1, path: '/' });
    this._hasSession.set(true);
  }

  getToken() {
    return getCookie(TOKEN_KEY);
  }

  removeToken() {
    removeCookie(TOKEN_KEY);
    this._hasSession.set(false);
  }

  /** Clears the user session and cached state, then reloads the application. */
  endSession() {
    this.removeToken();
    this._sessionEndCallbacks.forEach((callback) => callback());
    window.location.reload();
  }

  onSessionEnd(callback: () => void): void {
    this._sessionEndCallbacks.add(callback);
  }

  isTokenValid(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    const payload = decodeJwt(token);
    return payload !== null && !isTokenExpired(payload);
  }
}
